import { Alert, Button, Form, Input, InputNumber, Modal, Select, Table, Tag, Tooltip, message } from 'antd';
import { get, isEmpty, isEqual, keyBy } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { ADMIN_INVESTMENT_RULES } from '../../pages/apollo_queries';
import { ADMIN_SET_INVESTMENT_RULE } from '../../pages/apollo_mutations';
import { TableMainLabel } from '../styled';
import { MONEY_INPUT, PRODUCTS, REASON_RULES, backendMessage, fmtMoney, productLabel } from './shared';

// Tab 1 — one row per product, the STORED rule (`adminInvestmentRules`). Edit is a PATCH: only what
// changed travels, with a mandatory reason; the backend validates the merged row.

const EDITABLE = ['firstTimeMinimum', 'subsequentMinimum', 'presets', 'defaultAmount'];

// Chips as typed in the tags Select -> numbers (NaN for anything that is not one).
const toPresets = (chips) => (chips || []).map((chip) => Number(`${chip}`.replace(/[$,\s]/g, '')));

// What changed vs the stored row — the only fields the mutation will carry.
function diffRule(rule, values) {
  const patch = {};
  EDITABLE.forEach((field) => {
    const next = field === 'presets' ? toPresets(values.presets) : values[field];
    if (!isEqual(next, rule[field])) patch[field] = next;
  });
  return patch;
}

const positive = (label) => ({
  validator: (_, value) =>
    typeof value === 'number' && value > 0
      ? Promise.resolve()
      : Promise.reject(new Error(`${label} must be greater than 0`)),
});

const validatePresets = (_, chips) => {
  const presets = toPresets(chips);
  if (presets.length > 8) return Promise.reject(new Error('At most 8 presets'));
  if (presets.some((p) => !Number.isFinite(p) || p <= 0)) {
    return Promise.reject(new Error('Every preset must be a number greater than 0'));
  }
  if (presets.some((p, i) => i > 0 && p <= presets[i - 1])) {
    return Promise.reject(new Error('Presets must be unique and in ascending order'));
  }
  return Promise.resolve();
};

function RuleEditModal({ rule, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [patch, setPatch] = useState({});
  const [error, setError] = useState(null);
  const [setRule, { loading }] = useMutation(ADMIN_SET_INVESTMENT_RULE);
  const label = productLabel(rule.productType);

  const submit = async ({ reason }) => {
    setError(null);
    try {
      await setRule({
        variables: { data: { productType: rule.productType, reason: reason.trim(), ...patch } },
      });
      await onSaved();
      message.success(`${label} rule updated`);
      onClose();
    } catch (e) {
      setError(backendMessage(e));
    }
  };

  return (
    <Modal
      visible
      title={`Edit rule · ${label}`}
      okText="Save"
      okButtonProps={{ loading, disabled: isEmpty(patch) }}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnClose
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
        preserve={false}
        initialValues={{
          firstTimeMinimum: rule.firstTimeMinimum,
          subsequentMinimum: rule.subsequentMinimum,
          presets: rule.presets.map(String),
          defaultAmount: rule.defaultAmount,
          reason: '',
        }}
        onValuesChange={(_, all) => {
          setPatch(diffRule(rule, all));
          setError(null);
        }}
      >
        <Form.Item
          name="firstTimeMinimum"
          label="First-time minimum"
          extra="No completed investment in this product yet, on any rail"
          rules={[positive('First-time minimum')]}
        >
          <InputNumber {...MONEY_INPUT} />
        </Form.Item>
        <Form.Item
          name="subsequentMinimum"
          label="Subsequent minimum"
          rules={[positive('Subsequent minimum')]}
        >
          <InputNumber {...MONEY_INPUT} />
        </Form.Item>
        <Form.Item
          name="presets"
          label="Presets (widget chips)"
          extra="Up to 8 amounts, ascending, no repeats. Type a number and press Enter."
          rules={[{ validator: validatePresets }]}
        >
          <Select
            mode="tags"
            tokenSeparators={[',', ' ']}
            notFoundContent={null}
            placeholder="e.g. 1000, 5000, 10000"
          />
        </Form.Item>
        <Form.Item
          name="defaultAmount"
          label="Default amount"
          extra="Preselected in the widget; cannot be below the lowest minimum"
          dependencies={['firstTimeMinimum', 'subsequentMinimum']}
          rules={[
            positive('Default amount'),
            ({ getFieldValue }) => ({
              validator: (_, value) => {
                const floor = Math.min(
                  getFieldValue('firstTimeMinimum'),
                  getFieldValue('subsequentMinimum')
                );
                return typeof value === 'number' && Number.isFinite(floor) && value < floor
                  ? Promise.reject(
                      new Error(`Default amount cannot be below the lowest minimum (${fmtMoney(floor)})`)
                    )
                  : Promise.resolve();
              },
            }),
          ]}
        >
          <InputNumber {...MONEY_INPUT} />
        </Form.Item>
        <Form.Item name="reason" label="Reason (audited)" rules={REASON_RULES}>
          <Input.TextArea rows={3} placeholder="e.g. Approved by the investment committee, 2026-09" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// A product without a row spans the four amount cells with one notice.
const amountCell = (render) => ({
  render: ({ rule }) => (rule ? render(rule) : null),
  onCell: ({ rule }) => ({ colSpan: rule ? 1 : 0 }),
});

const columns = (onEdit) => [
  {
    title: 'Product',
    dataIndex: 'label',
    render: (label) => <TableMainLabel>{label}</TableMainLabel>,
  },
  {
    title: 'First-time minimum',
    align: 'right',
    render: ({ rule }) =>
      rule ? fmtMoney(rule.firstTimeMinimum) : <Tag>No minimum configured</Tag>,
    onCell: ({ rule }) => ({ colSpan: rule ? 1 : 4, align: rule ? 'right' : 'left' }),
  },
  { title: 'Subsequent minimum', align: 'right', ...amountCell((rule) => fmtMoney(rule.subsequentMinimum)) },
  {
    title: 'Presets',
    ...amountCell((rule) =>
      rule.presets.length ? rule.presets.map((p) => <Tag key={p}>{fmtMoney(p)}</Tag>) : '—'
    ),
  },
  { title: 'Default amount', align: 'right', ...amountCell((rule) => fmtMoney(rule.defaultAmount)) },
  {
    title: 'Last edited',
    render: ({ rule }) =>
      rule && rule.updatedAt ? (
        <Tooltip title={moment(rule.updatedAt).format('LLL')}>{moment(rule.updatedAt).fromNow()}</Tooltip>
      ) : (
        '—'
      ),
  },
  {
    width: 80,
    render: ({ rule }) => (
      <Tooltip title={rule ? '' : 'No rule row for this product: the catalogue is seeded by the backend'}>
        <Button size="small" disabled={!rule} onClick={() => onEdit(rule)}>
          Edit
        </Button>
      </Tooltip>
    ),
  },
];

export function RulesByProduct() {
  const { data, loading, error, refetch } = useQuery(ADMIN_INVESTMENT_RULES, {
    fetchPolicy: 'network-only',
  });
  const [editing, setEditing] = useState(null);

  if (error) {
    return <Alert type="error" showIcon message={backendMessage(error)} />;
  }

  const byType = keyBy(get(data, 'adminInvestmentRules', []), 'productType');
  const rows = PRODUCTS.map((product) => ({ ...product, rule: byType[product.type] || null }));

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="First-time = the investor has no completed investment in that product yet, on any rail. An exception granted to an investor (next tab) wins over the product rule. A change is live on the next request here and within a minute everywhere."
      />
      <Table
        rowKey="type"
        size="middle"
        columns={columns(setEditing)}
        dataSource={rows}
        loading={loading}
        pagination={false}
      />
      {editing && (
        <RuleEditModal rule={editing} onClose={() => setEditing(null)} onSaved={refetch} />
      )}
    </>
  );
}
